<template>
  <div class="external-event-participants-grid">
    <grid
      ref="grid"
      :show-header="true"
      :list-url="listUrl"
      loading-message="Loading participants ..."
      :add-checkmarks="true"
      :edit-action-as-link="false"
      @header-action="onGridHeaderAction"
      @item-action="onGridItemAction"
      @links-loaded="onGridLinksLoaded"
    >
      <template #custom-cell="cellObj">
        <a
          v-if="cellObj.item.links.download"
          :href="cellObj.item.links.download"
          title="Download certificate"
        >
          <span class="glyphicons download" />
        </a>
      </template>
    </grid>
    <teleport
      to="#vue-portal-target"
      append
    >
      <modal
        ref="modal"
        :form-url="modalUrl"
        :modal-title="modalTitle"
        @success="onModalSuccess"
      />
      <modal
        ref="sendModal"
        modal-title="E-mail certificates"
        :form-url="sendModalProps.formUrl"
        :form-params="sendModalProps.formParams"
        @remote-form-loaded="onSendModalFormLoaded"
      />
      <external-event-participants-upload-modal
        ref="uploadModal"
        :upload-url="uploadUrl"
        :template-url="uploadTemplateUrl"
      />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import {nextTick, ref, useTemplateRef} from 'vue'

import $ from 'jquery'
import URI from 'urijs'

import ExternalEventParticipantsUploadModal from './external-event-participants-upload-modal.vue'
import Grid from './grid.vue'
import Modal from './modal.vue'

import type {ButtonAction, GridHeaderAction, GridID, GridItem, ItemAction} from './types'
import {isNonEmptyString} from '../js-utils'

interface GridActionResponse {
  message?: unknown
  error?: unknown
}

defineProps({
  listUrl: {default: null, type: String}
})

const grid = useTemplateRef<InstanceType<typeof Grid>>('grid')
const modal = useTemplateRef<InstanceType<typeof Modal>>('modal')
const sendModal = useTemplateRef<InstanceType<typeof Modal>>('sendModal')
const uploadModal = useTemplateRef<InstanceType<typeof ExternalEventParticipantsUploadModal>>('uploadModal')

const modalUrl = ref<string>()
const modalTitle = ref<string>()
const uploadUrl = ref<string>()
const uploadTemplateUrl = ref<string>()
const sendModalProps = ref({
  formUrl: undefined as string | undefined,
  formParams: {
    participant_ids: [] as GridID[]
  }
})

const selectedParticipantIds = (): GridID[] => {
  return grid.value?.selectedItemIds || []
}

const onGridLinksLoaded = (links: Record<string, string>): void => {
  uploadUrl.value = links['upload-url']
  uploadTemplateUrl.value = links['upload-template-url']
}

const onGridHeaderAction = (actionOrName: GridHeaderAction | ButtonAction | string, url?: string): void => {
  const name = typeof actionOrName === 'string' ? actionOrName : actionOrName.name
  const actionUrl = typeof actionOrName === 'string' ? url : actionOrName.url

  if (name === 'new') {
    modalUrl.value = actionUrl
    modalTitle.value = 'New participant'
    nextTick(() => {
      modal.value?.showModal()
    })
  } else if (name === 'upload') {
    uploadModal.value?.showModal()
  } else if (name === 'generate') {
    const participantIds = selectedParticipantIds()
    if (participantIds.length == 0) {
      alert('Please select at least one participant')
      return
    }
    if (!isNonEmptyString(actionUrl)) {
      return
    }

    grid.value?.performRequest(actionUrl, {participant_ids: participantIds}, {method: 'POST'}, (status: boolean, data?: GridActionResponse) => {
      if (status) {
        if (data && isNonEmptyString(data.message)) {
          grid.value?.showMessage(data.message)
        } else if (data && isNonEmptyString(data.error)) {
          grid.value?.showError(data.error)
        } else {
          grid.value?.showError('Invalid response from server')
        }
      }
    })
  } else if (name === 'download') {
    const participantIds = selectedParticipantIds()
    if (participantIds.length == 0) {
      alert('Please select at least one participant')
      return
    }
    if (!isNonEmptyString(actionUrl)) {
      return
    }

    const uri = new URI(actionUrl)
    uri.search({
      participant_ids: participantIds.join(',')
    })
    window.location.href = uri.toString()
  } else if (name === 'email') {
    const participantIds = selectedParticipantIds()
    if (participantIds.length == 0) {
      alert('Please select at least one participant')
      return
    }
    if (!isNonEmptyString(actionUrl)) {
      return
    }

    sendModalProps.value.formParams.participant_ids = participantIds
    sendModalProps.value.formUrl = actionUrl
    nextTick(() => {
      sendModal.value?.showModal()
    })
  }
}

const onGridItemAction = (item: GridItem, action: ItemAction): void => {
  if (action.name === 'edit') {
    modalUrl.value = item.links?.edit
    modalTitle.value = 'Edit participant'
    nextTick(() => {
      modal.value?.showModal()
    })
  }
}

const onModalSuccess = (): void => {
  modal.value?.hideModal()
  grid.value?.reload()
}

const onSendModalFormLoaded = (): void => {
  $(sendModal.value?.$el).find('button[ref="sendCertificatesButton"]').on('click', (e) => {
    e.preventDefault()

    const participantIds = selectedParticipantIds()
    if (isNonEmptyString(sendModalProps.value.formUrl)) {
      sendModal.value?.performRequest(sendModalProps.value.formUrl, {
        participant_ids: participantIds,
        send: 1
      }, null, (status, responseData) => {
        if (status) {
          sendModal.value?.setupForm(responseData)
        }
        grid.value?.reload()
      })
    }
  })
}
</script>
